import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe, StripeCardElementOptions } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {
  Container,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
} from '@mui/icons-material';

// Initialize Stripe with environment variable - only if key exists and in HTTPS
import { shouldLoadStripe } from '../../utils/apiConfig';

const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
// Only load Stripe if we have a key AND we're in HTTPS (required for production)
const stripePromise = (stripeKey && shouldLoadStripe()) ? loadStripe(stripeKey) : null;

// Diagnostic logging
if (process.env.NODE_ENV === 'development') {
  console.log('🔍 Stripe Configuration Check:');
  console.log('  - Key exists:', !!stripeKey);
  console.log('  - Key preview:', stripeKey ? `${stripeKey.substring(0, 20)}...` : 'Not set');
  console.log('  - Should load:', shouldLoadStripe());
  console.log('  - Stripe promise:', stripePromise ? 'Created' : 'NULL - Payment forms will not work');
  
  if (!stripeKey) {
    console.error('❌ REACT_APP_STRIPE_PUBLISHABLE_KEY is missing!');
    console.log('   Make sure client/.env file exists with: REACT_APP_STRIPE_PUBLISHABLE_KEY=pk_test_...');
  } else if (!stripePromise) {
    console.warn('⚠️  Stripe promise is null. Check shouldLoadStripe() condition.');
  } else {
    console.log('✅ Stripe is configured correctly');
  }
}

interface Channel {
  id: string;
  name: string;
  tier: number;
  price: number;
  country: string;
  description: string;
}

interface CheckoutProps {}

// Stripe Payment Form Component
const StripePaymentForm: React.FC<{
  amount: number;
  billingInfo: any;
  selectedChannels: Channel[];
  onSuccess: (paymentIntent: any) => void;
}> = ({ amount, billingInfo, selectedChannels, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please wait.');
      return;
    }

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setError('Card element not found');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Step 1: Create payment intent on your backend
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${apiUrl}/api/payments/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: Math.round(amount * 100), // Convert to cents
          channels: selectedChannels,
          billingInfo: billingInfo,
          currency: 'usd',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create payment intent');
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      const { clientSecret } = data;

      // Step 2: Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: `${billingInfo.firstName} ${billingInfo.lastName}`,
              email: billingInfo.email,
              address: {
                line1: billingInfo.address,
                city: billingInfo.city,
                state: billingInfo.state,
                postal_code: billingInfo.zipCode,
                country: billingInfo.country,
              },
            },
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        setSucceeded(true);
        setProcessing(false);
        
        // Track successful payment with Google Analytics
        if ((window as any).gtag) {
          (window as any).gtag('event', 'purchase', {
            transaction_id: paymentIntent.id,
            value: amount,
            currency: 'USD',
            items: selectedChannels.map(ch => ({
              item_id: ch.id,
              item_name: ch.name,
              price: ch.price,
            })),
          });
        }

        // Call success callback after short delay
        setTimeout(() => {
          onSuccess(paymentIntent);
        }, 1500);
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  const cardElementOptions: StripeCardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        '::placeholder': {
          color: '#aab7c4',
        },
        iconColor: '#666',
      },
      invalid: {
        color: '#9e2146',
        iconColor: '#9e2146',
      },
    },
    hidePostalCode: true,
  };

  if (succeeded) {
    return (
      <Alert severity="success" icon={<PaymentIcon style={{ fontSize: '40px' }} />}>
        <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
          Payment Successful!
        </Typography>
        <Typography variant="body2">
          Your press release distribution is being processed...
        </Typography>
        <Typography variant="caption" color="text.secondary" style={{ marginTop: '8px', display: 'block' }}>
          Redirecting to distribution status...
        </Typography>
      </Alert>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Card style={{ marginBottom: '24px' }}>
        <CardContent>
          <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
            <LockIcon style={{ marginRight: '8px', color: '#4caf50' }} />
            <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
              Secure Payment Processing
            </Typography>
          </Box>

          <Alert severity="info" style={{ marginBottom: '24px' }}>
            <Typography variant="body2">
              <strong>Test Card:</strong> Use <strong>4242 4242 4242 4242</strong> with any future expiry date and any 3-digit CVC
            </Typography>
          </Alert>

          <Box
            style={{
              padding: '16px',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              backgroundColor: '#fafafa',
              marginBottom: '16px',
            }}
          >
            <CardElement options={cardElementOptions} />
          </Box>

          {error && (
            <Alert severity="error" style={{ marginBottom: '16px' }}>
              {error}
            </Alert>
          )}

          <Box style={{ marginTop: '32px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            <Typography variant="h6" gutterBottom>
              Payment Summary
            </Typography>
            <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <Typography variant="body1">Total Amount:</Typography>
              <Typography variant="h5" style={{ fontWeight: 'bold', color: '#F36409' }}>
                ${amount.toFixed(2)}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {selectedChannels.length} publication{selectedChannels.length > 1 ? 's' : ''} selected
            </Typography>
          </Box>

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={!stripe || processing}
            style={{
              marginTop: '24px',
              backgroundColor: processing || !stripe ? '#cccccc' : '#F36409',
              color: '#ffffff',
              padding: '16px',
              fontSize: '18px',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(243, 100, 9, 0.3)',
              cursor: processing || !stripe ? 'not-allowed' : 'pointer',
            }}
          >
            {processing ? (
              <>
                <CircularProgress size={24} style={{ marginRight: '12px', color: '#ffffff' }} />
                Processing Payment...
              </>
            ) : !stripe ? (
              'Loading Stripe...'
            ) : (
              <>
                <PaymentIcon style={{ marginRight: '8px' }} />
                Pay ${amount.toFixed(2)}
              </>
            )}
          </Button>

          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: '16px',
            }}
          >
            <SecurityIcon style={{ fontSize: '16px', marginRight: '4px', color: '#666' }} />
            <Typography variant="caption" color="text.secondary">
              Powered by Stripe • PCI DSS Compliant • 256-bit SSL Encryption
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </form>
  );
};

const Checkout: React.FC<CheckoutProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [billingInfo, setBillingInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US'
  });

  const selectedChannels: Channel[] = location.state?.selectedChannels || [];
  const prTitle: string = location.state?.prTitle || 'Press Release Distribution';

  const steps = ['Order Summary', 'Billing Details', 'Payment'];

  const totalAmount = selectedChannels.reduce((sum, channel) => sum + channel.price, 0);
  const taxAmount = totalAmount * 0.08;
  const finalAmount = totalAmount + taxAmount;

  const getTierName = (tier: number) => {
    const tiers = ['', 'Tier 1', 'Tier 2', 'Tier 3', 'Tier 4', 'Tier 5'];
    return tiers[tier] || `Tier ${tier}`;
  };

  const getTierColor = (tier: number) => {
    switch (tier) {
      case 1: return '#d32f2f';
      case 2: return '#f57c00';
      case 3: return '#1976d2';
      case 4: return '#388e3c';
      case 5: return '#7b1fa2';
      default: return '#666666';
    }
  };

  const handleNext = () => {
    if (activeStep === 1) {
      const requiredFields = ['firstName', 'lastName', 'email', 'address', 'city', 'state', 'zipCode', 'country'];
      const isValid = requiredFields.every(field => billingInfo[field as keyof typeof billingInfo]);
      
      if (!isValid) {
        alert('Please fill in all required billing information');
        return;
      }
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleBillingChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setBillingInfo({
      ...billingInfo,
      [field]: event.target.value
    });
  };

  const handlePaymentSuccess = async (paymentIntent: any) => {
    // Navigate to distribution status page
    navigate('/distribution', {
      state: {
        paymentCompleted: true,
        paymentData: {
          amount: finalAmount,
          channels: selectedChannels,
          billingInfo,
          transactionId: paymentIntent.id,
          paymentIntentId: paymentIntent.id,
          status: paymentIntent.status
        }
      }
    });
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold', marginBottom: '24px' }}>
              Order Summary
            </Typography>
            <Alert severity="info" style={{ marginBottom: '24px' }}>
              <Typography variant="body2">
                <strong>{prTitle}</strong>
              </Typography>
              <Typography variant="caption">
                Distribution to {selectedChannels.length} publication{selectedChannels.length > 1 ? 's' : ''}
              </Typography>
            </Alert>
            
            <List>
              {selectedChannels.map((channel, index) => (
                <Paper key={index} elevation={2} style={{ marginBottom: '12px', padding: '16px' }}>
                  <ListItem style={{ padding: 0 }}>
                    <ListItemText
                      primary={
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                            {channel.name}
                          </Typography>
                          <Box
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: getTierColor(channel.tier)
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <React.Fragment>
                          <Typography component="span" variant="body2" color="text.secondary" display="block">
                            {getTierName(channel.tier)} • {channel.country}
                          </Typography>
                          <Typography component="span" variant="body2" color="text.secondary" display="block" style={{ marginTop: '4px' }}>
                            {channel.description}
                          </Typography>
                        </React.Fragment>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Typography variant="h6" style={{ fontWeight: 'bold', color: '#F36409' }}>
                        ${channel.price.toLocaleString()}
                      </Typography>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Paper>
              ))}
            </List>

            <Divider style={{ margin: '24px 0' }} />

            <Paper elevation={0} style={{ padding: '16px', backgroundColor: '#f5f5f5' }}>
              <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <Typography variant="body1">Subtotal:</Typography>
                <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                  ${totalAmount.toLocaleString()}
                </Typography>
              </Box>
              <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <Typography variant="body1">Tax (8%):</Typography>
                <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                  ${taxAmount.toFixed(2)}
                </Typography>
              </Box>
              <Divider style={{ margin: '12px 0' }} />
              <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" style={{ fontWeight: 'bold' }}>Total:</Typography>
                <Typography variant="h5" style={{ fontWeight: 'bold', color: '#F36409' }}>
                  ${finalAmount.toFixed(2)}
                </Typography>
              </Box>
            </Paper>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold', marginBottom: '24px' }}>
              Billing Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="First Name"
                  value={billingInfo.firstName}
                  onChange={handleBillingChange('firstName')}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Last Name"
                  value={billingInfo.lastName}
                  onChange={handleBillingChange('lastName')}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={billingInfo.email}
                  onChange={handleBillingChange('email')}
                  variant="outlined"
                  helperText="Receipt will be sent to this email"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Name (Optional)"
                  value={billingInfo.company}
                  onChange={handleBillingChange('company')}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Street Address"
                  value={billingInfo.address}
                  onChange={handleBillingChange('address')}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="City"
                  value={billingInfo.city}
                  onChange={handleBillingChange('city')}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  required
                  fullWidth
                  label="State/Province"
                  value={billingInfo.state}
                  onChange={handleBillingChange('state')}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  required
                  fullWidth
                  label="ZIP/Postal Code"
                  value={billingInfo.zipCode}
                  onChange={handleBillingChange('zipCode')}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Country Code (e.g., US, IN, GB)"
                  value={billingInfo.country}
                  onChange={handleBillingChange('country')}
                  variant="outlined"
                  inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold', marginBottom: '24px' }}>
              Payment Details
            </Typography>
            
            {stripePromise ? (
              <Elements stripe={stripePromise}>
                <StripePaymentForm
                  amount={finalAmount}
                  billingInfo={billingInfo}
                  selectedChannels={selectedChannels}
                  onSuccess={handlePaymentSuccess}
                />
              </Elements>
            ) : (
              <Alert severity="error">
                Stripe is not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY environment variable.
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  if (selectedChannels.length === 0) {
    return (
      <Container maxWidth="md" style={{ marginTop: '40px' }}>
        <Alert severity="warning" style={{ marginBottom: '24px' }}>
          <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            No Publications Selected
          </Typography>
          <Typography variant="body2">
            Please go back to the distribution page and select publications for your press release.
          </Typography>
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/distribution')}
          startIcon={<ArrowBackIcon />}
          style={{ 
            backgroundColor: '#F36409', 
            color: '#ffffff',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          Back to Distribution
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" style={{ marginTop: '40px', marginBottom: '60px' }}>
      <Paper 
        elevation={3} 
        style={{ 
          padding: '40px',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px'
        }}
      >
        <Typography variant="h3" gutterBottom align="center" style={{ fontWeight: 'bold', marginBottom: '32px' }}>
          Secure Checkout
        </Typography>
        
        <Stepper activeStep={activeStep} style={{ marginBottom: '48px' }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box style={{ marginBottom: '32px' }}>
          {renderStepContent(activeStep)}
        </Box>

        {activeStep < 2 && (
          <Box style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '24px', borderTop: '1px solid #e0e0e0' }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              size="large"
              style={{
                padding: '12px 24px'
              }}
            >
              Back
            </Button>
            
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForwardIcon />}
              size="large"
              style={{
                backgroundColor: '#F36409',
                color: '#ffffff',
                padding: '12px 32px',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              {activeStep === steps.length - 2 ? 'Proceed to Payment' : 'Next Step'}
            </Button>
          </Box>
        )}
        
        {activeStep === 2 && (
          <Box style={{ display: 'flex', justifyContent: 'flex-start', paddingTop: '24px', borderTop: '1px solid #e0e0e0' }}>
            <Button
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              size="large"
              style={{
                padding: '12px 24px'
              }}
            >
              Back to Billing
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default Checkout;
